import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api, ASSETS } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState(token ? "loading" : "missing");
  const [alreadyVerified, setAlreadyVerified] = useState(false);
  const auth = useAuth();

  useEffect(() => {
    if (!token) return;
    let done = false;
    (async () => {
      try {
        const r = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        if (done) return;
        setAlreadyVerified(Boolean(r.data?.already_verified));
        setStatus("ok");
        // Refresh the local user session so the ORIGINAL logged-in tab
        // stops showing the "verify your email" banner.  Runs regardless
        // of whether THIS tab is authenticated — if /auth/me returns
        // 401 (no session in this tab / webview), we silently skip.
        try {
          const me = await api.get("/auth/me");
          if (me?.data) {
            localStorage.setItem("djspy_user", JSON.stringify(me.data));
            window.dispatchEvent(new Event("djspy-user-refreshed"));
          }
        } catch (_) {}
      } catch (err) {
        if (done) return;
        const detail = err?.response?.data?.detail;
        setStatus(detail === "expired_token" ? "expired"
                : detail === "token_already_used" ? "used"
                : "invalid");
      }
    })();
    return () => { done = true; };
  }, [token, auth]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden bg-black" data-testid="verify-email-page">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(239,43,43,0.15),transparent_60%)]" />
      <div className="w-full max-w-md relative">
        <Link to="/" className="font-mono text-[10px] tracking-[0.3em] text-white/50 hover:text-white">← BACK TO SITE</Link>
        <div className="mt-6 hairline rounded-2xl bg-white/[0.02] p-10 text-white">
          <img src={ASSETS.logo} alt="DJ SPY" className="h-12 w-auto" />
          <div className="mt-6 font-mono text-[10px] tracking-[0.3em] text-red-brand">// EMAIL VERIFICATION</div>
          {status === "loading" && (
            <div className="mt-6 flex items-center gap-3 text-white/70" data-testid="verify-loading">
              <Loader2 className="animate-spin" size={20} /> Confirming your email…
            </div>
          )}
          {status === "ok" && (
            <div className="mt-6 space-y-4" data-testid="verify-ok">
              <div className="flex items-center gap-3 text-emerald-400">
                <CheckCircle2 size={28} />
                <div className="font-anton text-2xl">
                  {alreadyVerified ? "Already verified" : "Email verified"}
                </div>
              </div>
              <p className="text-white/70">You can now checkout, download purchases, and manage your account.</p>
              <Link to="/library" className="btn-primary" data-testid="verify-goto-library">Open my library</Link>
            </div>
          )}
          {(status === "expired" || status === "invalid" || status === "used") && (
            <div className="mt-6 space-y-4" data-testid={`verify-${status}`}>
              <div className="flex items-center gap-3 text-red-brand">
                <XCircle size={28} />
                <div className="font-anton text-2xl">
                  {status === "expired" ? "Link expired" : status === "used" ? "Link already used" : "Invalid link"}
                </div>
              </div>
              <p className="text-white/70">Sign in and request a new verification email from your library.</p>
              <Link to="/login" className="btn-primary" data-testid="verify-go-login">Sign in</Link>
            </div>
          )}
          {status === "missing" && (
            <div className="mt-6 space-y-4" data-testid="verify-missing">
              <p className="text-white/70">This page needs a verification token from the email we sent you.</p>
              <Link to="/login" className="btn-primary">Sign in</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
